import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingRule } from "react-table";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import TableComponent, { OperationsCell } from "../../components/TableComponent";
import Routes from "../../helpers/Routes";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";
import { ClientDTO } from "../../models/Client/ClientDTO";
import { Parameter } from "../../models/Parameter";
import { ClientService } from "../../services/ClientService";

export default function ClientIndexDefault() {
    const intl = useIntl();
    const navigate = useNavigate();
    const [name, setName] = useState<string>(
        Utilities.LoadParameterFromURLQuery("name", "string", ""),
    );
    const [erpId, setErpId] = useState<string>(
        Utilities.LoadParameterFromURLQuery("erpId", "string", ""),
    );
    const [data, setData] = useState<ClientDTO[]>([]);
    const clientService = new ClientService();
    const [initialSortBy] = useState<SortingRule<object>[]>(
        Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder"),
    );
    const pageSizes: number[] = [5, 15, 30];
    const [goToPage, setGoToPage] = useState<number>();
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(1);
    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [pageSize] = useState<number>(Utilities.LoadPageSizeFromURLQuery(pageSizes));
    const [currentPage, setCurrentPage] = useState<number>();
    const [currentPageSize, setCurrentPageSize] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<SortingRule<object>[] | null>(null);
    const [reloadSearch, setReloadSearch] = useState<boolean>(false);
    const [searchParams] = useSearchParams();

    const onEnter = () => {
        setCurrentPage(1);
        setGoToPage(1);
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };
    const onButtonClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };


    const columns = React.useMemo(
        () => [
            {
                Header: "",
                accessor: " ",
                Cell: (props: any) => OperationsCell(props.cell.row.original.erpId, "client"),
            },
            {
                Header: <FormattedMessage id="Screens.Clients.Table.Column1" />,
                accessor: "erpId",
                width: 15,
            },
            {
                Header: <FormattedMessage id="Screens.Clients.Table.Column2" />,
                accessor: "name",
                width: 35,
            },
            {
                Header: <FormattedMessage id="Screens.Clients.Table.Column3" />,
                accessor: "vatNumber",
                width: 15,
            },
            {
                Header: <FormattedMessage id="Screens.Clients.Table.Column4" />,
                accessor: "email",
                width: 35,
            }
        ],
        [],
    );

    const fetchData = async (
        page: number,
        pageSize: number,
        sortBy: SortingRule<object>[] | null,
    ) => {
        //Atualizamos na history do browser a current page e pageSize

        var searchUrl: string =
            `?page=${page}&pageSize=${pageSize}${Utilities.AddParamToQueryIfValue(
                "name",
                "string",
                name,
            )}${Utilities.AddParamToQueryIfValue(
                "erpId",
                "string",
                erpId,
            )}`;

        if (sortBy != null && sortBy.length > 0) {
            searchUrl += `${Utilities.AddParamToQueryIfValue(
                "sortField",
                "string",
                sortBy[0].id,
            )}${Utilities.AddParamToQueryIfValue(
                "sortOrder",
                "string",
                sortBy[0].desc === true ? "DESC" : "ASC",
            )}`;
        }

        //Se antes não existirem parâmetros de pesquisa, significa que o user veio para aqui de uma rota, então para não ficar com duas rotas duplicadas, fazemos replace à rota

        if (searchParams.toString() === "") {
            navigate(`/clients${searchUrl}`, { replace: true });
        } else {
            //Se já existirem parâmetros de pesquisa, não fazemos replace mas push
            navigate(`/clients${searchUrl}`);
        }

        setLoading(true);

        //Vamos obter os dados
        var response = await clientService.GetAll(
            mountSearchParameters(),
            Utilities.MountSortParameters(sortBy),
            page,
            pageSize,
        );

        if (response.success !== true) {
            setData([]);
            setPageCount(0);
            setLoading(false);
            setCurrentPageSize(0);
            setCurrentPage(0);
            setCurrentSorting(null);
            Toast.Show("error", response.message);
            return;
        }

        //Atribuímos os dados
        setData(response.items);
        setGoToPage(0);
        setPageCount(response.totalPages);
        setLoading(false);
        setCurrentPageSize(pageSize);
        setCurrentPage(page);
        setCurrentSorting(sortBy);

        //Se a pesquisa tiver retornado múltiplas páginas e a página atual for superior ao nº de páginas que chegaram mandamos o user para a página 1
        if (response.totalPages > 0 && page > response.totalPages) {
            setGoToPage(1);
        }
    };

    //Método para montar os parâmetros de pesquisa
    const mountSearchParameters = (): Parameter[] => {
        var parameters: Parameter[] = [];

        parameters.push(new Parameter("name", name));
        parameters.push(new Parameter("erpId", erpId));
        return parameters;
    };

    const onRemoveFiltersClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        setName("");
        setErpId("");
        setReloadSearch(true);
    };

    useEffect(() => {
        if (reloadSearch === true) {
            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
            setReloadSearch(false);
        }
        return () => { };
    }, [reloadSearch]);

    const onNewTaskClick = () => {
        navigate(Routes.clients.create);
    };

    return (
        <>
            <Row>
                <InputComponent xl={2} lg={3} md={5} sm={12} xs={12}
                    style={{ width: "100%" }}
                    type={"text"}
                    onEnter={onEnter}
                    value={erpId}
                    label={intl.formatMessage({ id: "Screens.Clients.SearchField1" })}
                    readOnly={false}
                    onChange={(t: string) => setErpId(t)}
                />
                <InputComponent xl={2} lg={3} md={5} sm={12} xs={12}
                    style={{ width: "100%" }}
                    type={"text"}
                    onEnter={onEnter}
                    value={name}
                    label={intl.formatMessage({ id: "Screens.Clients.SearchField2" })}
                    readOnly={false}
                    onChange={(t: string) => setName(t)}
                />

            </Row>

            <Row>
                <Col xl={6} lg={6} md={6} sm={12} xs={12}
                    style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}
                >
                    <ButtonComponent
                        text={intl.formatMessage({ id: "Common.Buttons.Search" })}
                        onClick={onButtonClick}
                    />
                    <ButtonComponent
                        text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
                        onClick={onRemoveFiltersClick}
                    />
                </Col>
            </Row>

            <div className="pageComponent" style={{ width: "100%" }}>
                <TableComponent
                    goToPage={goToPage}
                    loading={loading}
                    pageCount={pageCount}
                    columns={columns}
                    manualPagination={true}
                    fetchData={fetchData}
                    pageSizes={pageSizes}
                    initialPage={page}
                    initialPageSize={pageSize}
                    initialSortBy={initialSortBy}
                    data={data}
                    dontOrderFirstCollumn={true}
                />
            </div>
        </>
    )
}